import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import FeesScholarshipsForm from '../components/forms/FeesScholarshipsForm';
import { adminAPI, publicAPI } from '../services/api';

const normalizeFeesScholarshipsContent = (raw) => {
  const content = raw && typeof raw === 'object' ? raw : {};

  return {
    fees: Array.isArray(content.fees)
      ? content.fees.map((fee) => ({
          program: fee?.program || '',
          annualFee: fee?.annualFee || '',
          totalFee: fee?.totalFee || '',
        }))
      : [],
    scholarships: Array.isArray(content.scholarships)
      ? content.scholarships.map((scholarship) => ({
          name: scholarship?.name || '',
          coverage: scholarship?.coverage || '',
          criteria: scholarship?.criteria || '',
        }))
      : [],
  };
};

const FeesScholarshipsAdminPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(() => normalizeFeesScholarshipsContent());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    const hasToken = localStorage.getItem('adminToken');
    if (!isLoggedIn || !hasToken) {
      navigate('/admin');
      return;
    }

    const loadContent = async () => {
      try {
        setLoading(true);
        const data = await publicAPI.getInternationalFeesScholarships();
        setContent(normalizeFeesScholarshipsContent(data));
      } catch (error) {
        setContent(normalizeFeesScholarshipsContent());
        toast.error('Failed to load fees and scholarships content');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [navigate]);

  const handleSave = async () => {
    try {
      setSaving(true);

      if (!Array.isArray(content?.fees) || !Array.isArray(content?.scholarships)) {
        toast.error('Fees and scholarships rows are required');
        return;
      }

      const hasInvalidFee = content.fees.some(
        (fee) => !fee.program?.trim() || !fee.annualFee?.trim() || !fee.totalFee?.trim()
      );
      if (hasInvalidFee) {
        toast.error('Each fee row requires Program, Annual Fee and Total Fee');
        return;
      }

      const hasInvalidScholarship = content.scholarships.some(
        (item) => !item.name?.trim() || !item.coverage?.trim() || !item.criteria?.trim()
      );
      if (hasInvalidScholarship) {
        toast.error('Each scholarship row requires Name, Coverage and Criteria');
        return;
      }

      await adminAPI.updateInternationalFeesScholarships({
        fees: content.fees,
        scholarships: content.scholarships,
      });
      toast.success('Fees & scholarships content updated');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.message || 'Failed to save fees and scholarships content');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>

      <FeesScholarshipsForm
        content={content}
        setContent={setContent}
        loading={loading}
        saving={saving}
        onSave={handleSave}
      />
    </div>
  );
};

export default FeesScholarshipsAdminPage;
